import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@common/toast';
import Icon from '@common/assets';
import Text from '@common/text';
import SubMenu from '@components/mypage/subMenu';
import UserDeleteModal from '@components/mypage/userDeleteModal';
import Profile from '@components/mypage/profile';
import { useLogout, useUserInfo } from '@repository/auth/useUserInfo';
import { useDeleteAccount, useSavePassword } from '@repository/users/useUsers';
import Colors from '@domain/constant/colors';
import { FontStyle } from '@domain/constant/fontStyle';
import { onInputPassword, passwordValidator, rePasswordValidator, valueRequired } from '@utils/validators/validators';
import useDebounce from '@utils/hooks/useDebounce';
import classnames from 'classnames/bind';
import styles from './account.module.scss';

const cx = classnames.bind(styles);

export interface IAccountInfoFormField {
  currentPassword: string;
  newPassword: string;
  rePassword: string;
}

const AccountInfo = () => {
  const Toast = useToast();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: logout } = useLogout();
  const { mutateAsync: updatePassword } = useSavePassword();
  const { mutateAsync: deleteAccount } = useDeleteAccount();

  const userName = userInfo?.name || '';
  const userId = userInfo?.id || '';
  const email = userInfo?.email || '';

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setError,
    formState: { isValid, dirtyFields },
  } = useForm<IAccountInfoFormField>({
    mode: 'onChange',
  });

  //패스위서 보이기/안보이기 상태값
  const [isOpenUserDeleteModal, setIsOpenUserDeleteModal] = useState<boolean>(false);
  const [isCurrentPwVisible, setIsCurrentPwVisible] = useState(false);
  const [isPwVisible, setIsPwVisible] = useState(false);
  const [isRePwVisible, setIsRePwVisible] = useState(false);

  const newPassword = watch('newPassword');
  const currentPassword = watch('currentPassword');
  const isDisabledSumbitBtn = !(Object.keys(dirtyFields).length === 3 && isValid);

  const onChangeIsCurrentPwVisible = useCallback(() => {
    setIsCurrentPwVisible(!isCurrentPwVisible);
  }, [isCurrentPwVisible]);

  const onChangeIsPwVisible = useCallback(() => {
    setIsPwVisible(!isPwVisible);
  }, [isPwVisible]);

  const onChangeIsRePwVisible = useCallback(() => {
    setIsRePwVisible(!isRePwVisible);
  }, [isRePwVisible]);

  const onSubmit = async (values: IAccountInfoFormField) => {
    try {
      const data = await updatePassword(values);
      if (data.success === true) {
        Toast({ type: 'success', iconType: 'check', content: '변경사항이 저장되었습니다.' });
        onInit();
      }
    } catch (e: any) {
      Toast({ type: 'error', iconType: 'info', content: '변경사항이 저장되지 않았습니다.' });
      switch (e?.response?.data?.errorCode) {
        case 'B901':
          setError('currentPassword', { message: '비밀번호가 틀립니다.' });
          break;
        case 'B902':
          setError('newPassword', { message: '8~16자, 영문 대,소문자, 숫자, 특수문자 중 2개 이상 사용하세요.' });
          break;
      }
    }
  };

  const onClickRemoveAccount = useCallback(() => {
    setIsOpenUserDeleteModal(true);
  }, []);

  const onCloseUserDeleteModal = useCallback(() => {
    setIsOpenUserDeleteModal(false);
  }, []);

  const onConfirmUserDeleteModal = useDebounce(async () => {
    try {
      const { success } = await deleteAccount();
      if (success) {
        Toast({
          type: 'success',
          size: 'xlarge',
          iconType: 'check',
          content: '계정이 정상적으로 삭제되었습니다.',
        });
        logout();
        setIsOpenUserDeleteModal(false);
      }
    } catch (e) {
      Toast({ type: 'error', iconType: 'info', content: '계정 삭제가 실패되었습니다.' });
    }
  }, 200);

  const onInit = useCallback(() => {
    reset({
      currentPassword: '',
      newPassword: '',
      rePassword: '',
    });
  }, []);

  return (
    <>
      {/* 상단 프로필 영역 */}
      <Profile userName={userName} profileImageUrl={userInfo?.profileImageUrl} />

      <div className={cx('wrap')}>
        {/* 좌측 메뉴 영역 */}
        <SubMenu page="account" onLogout={logout} />

        {/* 우측 정보 영역 - 계정정보 관리 */}
        <div className={cx('infoArea')}>
          <div className={cx('infoInner')}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <strong className={cx('title')}>비밀번호 변경</strong>

              <div className={cx('userInfoArea')}>
                <div className={cx('userInfoInner', 'typeText')}>
                  <span className={cx('infoTitle')}>아이디</span>
                  <span className={cx('infoText')}>{userId}</span>
                </div>

                <div className={cx('userInfoInner', 'typeInput')}>
                  <span className={cx('infoTitle')}>현재 비밀번호</span>
                  <div className={cx('userInputBox')}>
                    <input
                      tabIndex={1}
                      type={isCurrentPwVisible ? 'text' : 'password'}
                      className={cx('input', { error: errors.currentPassword })}
                      placeholder={'현재 비밀번호를 입력해 주세요.'}
                      maxLength={16}
                      onInput={onInputPassword}
                      {...register('currentPassword', {
                        ...valueRequired,
                        validate: (value: string) => passwordValidator(value, ''),
                      })}
                    />
                    <button
                      type="button"
                      className={cx('btnConfirmPassword', { on: isCurrentPwVisible })}
                      onClick={onChangeIsCurrentPwVisible}
                    />
                    {errors.currentPassword && (
                      <div className={cx('noticeText', 'error')}>{errors.currentPassword.message}</div>
                    )}
                  </div>
                </div>

                <div className={cx('userInfoInner', 'typePassword')}>
                  <span className={cx('infoTitle')}>새 비밀번호</span>
                  <div className={cx('userInputBox')}>
                    <input
                      tabIndex={2}
                      type={isPwVisible ? 'text' : 'password'}
                      className={cx('input', { error: errors.newPassword })}
                      placeholder={'새 비밀번호를 입력해 주세요.'}
                      minLength={6}
                      maxLength={16}
                      onInput={onInputPassword}
                      {...register('newPassword', {
                        ...valueRequired,
                        validate: (value: string) => passwordValidator(value, email, undefined, currentPassword),
                      })}
                    />
                    <button
                      type="button"
                      className={cx('btnConfirmPassword', { on: isPwVisible })}
                      onClick={onChangeIsPwVisible}
                    />
                    {errors.newPassword && (
                      <div className={cx('noticeText', 'error')}>{errors.newPassword.message}</div>
                    )}
                  </div>
                </div>

                <div className={cx('userInfoInner', 'typePassword')}>
                  <span className={cx('infoTitle')}>비밀번호 확인</span>
                  <div className={cx('userInputBox')}>
                    <input
                      tabIndex={3}
                      type={isRePwVisible ? 'text' : 'password'}
                      minLength={6}
                      maxLength={16}
                      onInput={onInputPassword}
                      className={cx('input', { error: errors.rePassword })}
                      placeholder="새 비밀번호를 다시 입력해 주세요."
                      {...register('rePassword', {
                        ...valueRequired,
                        validate: (value: string) => rePasswordValidator(value, newPassword),
                      })}
                    />
                    <button
                      type="button"
                      className={cx('btnConfirmPassword', { on: isRePwVisible })}
                      onClick={onChangeIsRePwVisible}
                    />
                    {errors.rePassword && <div className={cx('noticeText', 'error')}>{errors.rePassword.message}</div>}
                  </div>
                </div>
              </div>

              <button className={cx('btnSave')} type="submit" disabled={isDisabledSumbitBtn}>
                변경사항 저장
              </button>
            </form>
          </div>
          <div className={cx('infoInner')}>
            <strong className={cx('title')}>계정 삭제</strong>

            <div className={cx('textArea')}>
              <div className={cx('textWrap')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_ERROR} />
                <Text fontStyle={FontStyle.B3P_M} color={Colors.C_ERROR}>
                  서비스 부정 참여 방지를 위해 본인인증 정보 및 역검 응시 횟수는 90일간 보관됩니다.
                  <br />
                  <Text element={'span'} fontStyle={FontStyle.B3P_B} color={Colors.C_ERROR}>
                    따라서 90일 안에 재가입하실 경우, 계정 삭제 이전의 역검 응시 횟수가 유지됩니다.
                  </Text>
                </Text>
              </div>
              <div className={cx('textWrap')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_ERROR} />
                <Text fontStyle={FontStyle.B3P_M} color={Colors.C_ERROR}>
                  그 외의 모든 정보(역검 결과, 내 프로필, 지원 이력 등)는 완전히 삭제되며 복구될 수 없습니다.
                </Text>
              </div>
            </div>
            <a className={cx('btnDelete')} role="button" onClick={onClickRemoveAccount}>
              계정 삭제
            </a>
          </div>
        </div>

        {/* 계정 삭제 모달 */}
        <UserDeleteModal
          visible={isOpenUserDeleteModal}
          onClose={onCloseUserDeleteModal}
          onConfirm={onConfirmUserDeleteModal}
        />
      </div>
    </>
  );
};

export default AccountInfo;
